import {
    IconCheck, IconChevronLeft, IconChevronRight, IconCopy, IconDotsVertical, IconTrash,
} from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useState } from 'react';
import {
    Dropdown, DropdownItem, DropdownMenu, DropdownToggle,
} from 'reactstrap';
import { NSButton } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { MOVE_TO_SECTION_LABEL, NO_SECTION_LABEL } from 'views/Tasks/Timeline/constants';
import { TimelineSection } from 'views/Tasks/Timeline/types';
import './ActivityActionsMenu.scss';

type ActivityActionsMenuProps = {
    onDuplicate: () => void;
    onDelete: () => void;
    onMoveToSection: (sectionId: number | null) => void;
    sections: TimelineSection[];
    currentSectionId: number | null | undefined;
    dataTestIdPrefix: string;
};

const ActivityActionsMenu = ({
    onDuplicate, onDelete, onMoveToSection, sections, currentSectionId, dataTestIdPrefix,
}: ActivityActionsMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showSectionMenu, setShowSectionMenu] = useState(false);

    const toggle = () => {
        if (isOpen) setShowSectionMenu(false);
        setIsOpen(prev => !prev);
    };

    const sectionOptions: { id: number | null; label: string }[] = [
        { id: null, label: NO_SECTION_LABEL },
        ...sections.map(section => ({ id: section.id, label: section.name })),
    ];

    return (
        <Dropdown isOpen={isOpen} toggle={toggle} direction="down">
            <DropdownToggle tag="button" className="btn p-0 border-0 bg-transparent">
                <NSButton
                    color="secondary"
                    size="sm"
                    icon={IconDotsVertical}
                    ariaLabel="Activity actions"
                    className="ActivityActionsMenu__toggle"
                />
            </DropdownToggle>
            {/* Remount Popper when the pane changes so it repositions for the new height. */}
            <DropdownMenu end key={showSectionMenu ? 'sections' : 'root'}>
                <RenderIf isTrue={!showSectionMenu}>
                    <DropdownItem onClick={onDuplicate} className="text-white" data-testid={`${dataTestIdPrefix}-duplicate`}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                            <span>Duplicate task</span>
                            <NSIcon className="ml-1" icon={IconCopy} />
                        </div>
                    </DropdownItem>
                    <DropdownItem onClick={onDelete} className="text-white" data-testid={`${dataTestIdPrefix}-delete`}>
                        <div className="d-flex align-items-center justify-content-between w-100">
                            <span>Delete task</span>
                            <NSIcon className="ml-1" icon={IconTrash} />
                        </div>
                    </DropdownItem>
                    <RenderIf isTrue={sections.length > 0}>
                        <DropdownItem
                            toggle={false}
                            onClick={() => setShowSectionMenu(true)}
                            className="text-white"
                            data-testid={`${dataTestIdPrefix}-move-to-section`}
                        >
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <span>{MOVE_TO_SECTION_LABEL}</span>
                                <NSIcon className="ml-1" icon={IconChevronRight} />
                            </div>
                        </DropdownItem>
                    </RenderIf>
                </RenderIf>
                <RenderIf isTrue={showSectionMenu}>
                    <DropdownItem
                        toggle={false}
                        onClick={() => setShowSectionMenu(false)}
                        className="d-flex align-items-center text-primary"
                        data-testid={`${dataTestIdPrefix}-move-to-section-back`}
                    >
                        <NSIcon icon={IconChevronLeft} size="sm" className="mr-1" />
                        <span>Back</span>
                    </DropdownItem>
                    {sectionOptions.map(option => {
                        const isCurrent = (currentSectionId ?? null) === option.id;
                        return (
                            <DropdownItem
                                key={option.id ?? NO_SECTION_LABEL}
                                onClick={() => onMoveToSection(option.id)}
                                disabled={isCurrent}
                                className="text-white"
                                data-testid={`${dataTestIdPrefix}-move-to-section-option-${option.id ?? 'none'}`}
                            >
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span>{option.label}</span>
                                    <RenderIf isTrue={isCurrent}>
                                        <NSIcon className="ml-1" icon={IconCheck} />
                                    </RenderIf>
                                </div>
                            </DropdownItem>
                        );
                    })}
                </RenderIf>
            </DropdownMenu>
        </Dropdown>
    );
};

export default ActivityActionsMenu;
